<script lang="ts" setup>
import type { ShortCategoryType } from '#types';

const props = defineProps<{
  categories: ShortCategoryType[];
}>();
const visible = defineModel<boolean>('visible');
const includedCategories = defineModel<number[]>('included', {
  required: true,
});
const excludedCategories = defineModel<number[]>('excluded', {
  required: true,
});

const { t } = useI18n();

const localIncludedCategories = ref<number[]>([]);
const localExcludedCategories = ref<number[]>([]);

const options = computed(() =>
  props.categories.map((item) => ({
    label: item.label,
    value: item.id,
    included: localIncludedCategories.value.includes(item.id),
    excluded: localExcludedCategories.value.includes(item.id),
  })),
);

const onInclude = (value: number) => {
  if (!localIncludedCategories.value.includes(value))
    localIncludedCategories.value = [...localIncludedCategories.value, value];
  else localIncludedCategories.value = localIncludedCategories.value.filter((el) => el !== value);

  if (localExcludedCategories.value.includes(value))
    localExcludedCategories.value = localExcludedCategories.value.filter((el) => el !== value);
};

const onExclude = (value: number) => {
  if (!localExcludedCategories.value.includes(value))
    localExcludedCategories.value = [...localExcludedCategories.value, value];
  else localExcludedCategories.value = localExcludedCategories.value.filter((el) => el !== value);

  if (localIncludedCategories.value.includes(value))
    localIncludedCategories.value = localIncludedCategories.value.filter((el) => el !== value);
};

const onApply = () => {
  includedCategories.value = localIncludedCategories.value;
  excludedCategories.value = localExcludedCategories.value;

  visible.value = false;
};

watch(visible, () => {
  localIncludedCategories.value = includedCategories.value;
  localExcludedCategories.value = excludedCategories.value;
});
</script>

<template>
  <Dialog
    v-model:visible="visible"
    :header="t('title')"
    modal
    dismissable-mask
    :draggable="false"
    class="max-w-200"
    :pt="{ content: 'px-4!', footer: 'px-4!', header: 'px-4!' }"
  >
    <div class="flex gap-3 flex-wrap">
      <InputGroup v-for="item in options" :key="item.label" class="w-fit!">
        <Button
          @click="onInclude(item.value)"
          icon="pi pi-plus"
          :outlined="!item.included"
          :severity="item.included ? 'success' : 'secondary'"
          size="small"
          class="w-10!"
        />
        <Button
          @click="onExclude(item.value)"
          icon="pi pi-minus"
          :outlined="!item.excluded"
          :severity="item.excluded ? 'danger' : 'secondary'"
          size="small"
          class="w-10!"
        />
        <InputGroupAddon class="text-sm px-2!">
          {{ item.label }}
        </InputGroupAddon>
      </InputGroup>
    </div>

    <template #footer>
      <Button @click="visible = false" :label="t('cancel')" severity="secondary" />
      <Button @click="onApply" :label="t('apply')" />
    </template>
  </Dialog>
</template>

<i18n>
en:
  title: Setting up categories
  cancel: Cancel
  apply: Apply

ru:
  title: Настройка категорий
  cancel: Отменить
  apply: Применить

de:
  title: Kategorien einrichten
  cancel: Stornieren
  apply: Anwenden

es:
  title: Configurar categorías
  cancel: Cancelar
  apply: Aplicar

fr:
  title: Configuration des catégories
  cancel: Annuler
  apply: Appliquer
</i18n>
